import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { usersApi, rolesApi, userAdminApi } from '../services/api';
import { exportCSV } from '../utils/export';

const ROLE_COLORS = {
  player:      { bg: 'rgba(52, 152, 219, 0.15)',  color: '#3498db', label: 'Player' },
  dealer:      { bg: 'rgba(39, 174, 96, 0.15)',   color: '#27ae60', label: 'Dealer' },
  director:    { bg: 'rgba(243, 156, 18, 0.15)',  color: '#f39c12', label: 'Director' },
  leagueAdmin: { bg: 'rgba(102, 126, 234, 0.15)', color: '#667eea', label: 'League Admin' },
  siteAdmin:   { bg: 'rgba(192, 57, 43, 0.15)',   color: '#c0392b', label: 'Site Admin' },
};

function getRoleBadge(role) {
  const style = ROLE_COLORS[role] || { bg: 'rgba(127,140,141,0.15)', color: '#7f8c8d', label: role };
  return (
    <span
      key={role}
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        marginRight: 4,
      }}
    >
      {style.label}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPhone(phone) {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)})-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

function getInitials(user) {
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }
  return user.username?.[0]?.toUpperCase() || '?';
}

function Users({ user, onLogout }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Role editing
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingRoles, setEditingRoles] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [toast, setToast] = useState(null);

  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    password: '1234',
    roles: ['player'],
  });

  // Multi-select & archive
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchUsers = async (includeArchived) => {
    try {
      setLoading(true);
      const shouldIncludeArchived = includeArchived !== undefined ? includeArchived : showArchived;
      const res = shouldIncludeArchived ? await usersApi.getAllIncludingArchived() : await usersApi.getAll();
      const list = res.data?.users || res.users || [];
      setUsers(Array.isArray(list) ? list : []);
      setSelectedIds(new Set());
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await rolesApi.getAll();
      const list = res.data?.roles || res.roles || [];
      setAvailableRoles(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback to known roles
      setAvailableRoles([
        { name: 'player', description: 'Can sign up to play a game' },
        { name: 'dealer', description: 'Can start/pause/edit games' },
        { name: 'director', description: 'Can manage tournaments and oversee dealers' },
        { name: 'leagueAdmin', description: 'Can admin league and associated games' },
        { name: 'siteAdmin', description: 'Can admin everything' },
      ]);
    }
  };

  const filtered = users.filter(u => {
    const matchesSearch =
      !search ||
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.phoneNumber?.includes(search) ||
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase());

    const matchesRole =
      roleFilter === 'all' || (u.roles || []).includes(roleFilter);

    return matchesSearch && matchesRole;
  });

  const handleEditRoles = (userId) => {
    const userObj = users.find(u => u.id === userId);
    setEditingUserId(userId);
    setEditingRoles([...(userObj?.roles || [])]);
  };

  const handleToggleRole = (roleName) => {
    setEditingRoles(prev =>
      prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  const handleSaveRoles = async () => {
    if (!editingUserId) return;
    try {
      setSavingRoles(true);
      await usersApi.updateRoles(editingUserId, editingRoles);
      // Update local state
      setUsers(prev => prev.map(u =>
        u.id === editingUserId ? { ...u, roles: [...editingRoles] } : u
      ));
      const userObj = users.find(u => u.id === editingUserId);
      const name = userObj ? `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || userObj.username : '';
      setToast({ type: 'success', message: `Roles updated for ${name}` });
      setEditingUserId(null);
      setEditingRoles([]);
    } catch (err) {
      console.error('Error saving roles:', err);
      setToast({ type: 'error', message: 'Failed to update roles. Please try again.' });
    } finally {
      setSavingRoles(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingRoles([]);
  };

  // Create user handlers
  const handleOpenCreate = () => {
    setCreateForm({ firstName: '', lastName: '', phoneNumber: '', email: '', password: '1234', roles: ['player'] });
    setCreateError('');
    setShowCreateModal(true);
  };

  const handleCloseCreate = () => {
    setShowCreateModal(false);
    setCreateError('');
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateToggleRole = (roleName) => {
    setCreateForm(prev => ({
      ...prev,
      roles: prev.roles.includes(roleName)
        ? prev.roles.filter(r => r !== roleName)
        : [...prev.roles, roleName],
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');

    if (!createForm.firstName.trim() || !createForm.lastName.trim()) {
      setCreateError('First name and last name are required.');
      return;
    }
    if (!createForm.phoneNumber.trim()) {
      setCreateError('Phone number is required.');
      return;
    }
    if (!createForm.password || createForm.password.length < 4) {
      setCreateError('Password must be at least 4 characters.');
      return;
    }

    try {
      setCreating(true);
      await usersApi.create({
        firstName: createForm.firstName.trim(),
        lastName: createForm.lastName.trim(),
        phoneNumber: createForm.phoneNumber.trim(),
        email: createForm.email.trim() || undefined,
        password: createForm.password,
        roles: createForm.roles.length > 0 ? createForm.roles : ['player'],
      });
      handleCloseCreate();
      fetchUsers();
      setToast({ type: 'success', message: `User ${createForm.firstName} ${createForm.lastName} created` });
    } catch (err) {
      console.error('Error creating user:', err);
      setCreateError(err.message || 'Failed to create user.');
    } finally {
      setCreating(false);
    }
  };

  // Archive handlers
  const handleArchiveSelected = async () => {
    try {
      setArchiving(true);
      await usersApi.archive([...selectedIds]);
      setToast({ type: 'success', message: `${selectedIds.size} user(s) archived` });
      setShowArchiveConfirm(false);
      setSelectedIds(new Set());
      fetchUsers(showArchived);
    } catch (err) {
      console.error('Error archiving users:', err);
      setToast({ type: 'error', message: 'Failed to archive users' });
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async (userId) => {
    try {
      await usersApi.unarchive([userId]);
      setToast({ type: 'success', message: 'User unarchived' });
      fetchUsers(showArchived);
    } catch (err) {
      console.error('Error unarchiving users:', err);
      setToast({ type: 'error', message: 'Failed to unarchive user' });
    }
  };

  const handleToggleShowArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    fetchUsers(next);
  };

  const handleToggleSelect = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(u => u.id)));
    }
  };

  const allRoles = [...new Set(users.flatMap(u => u.roles || []))].sort();

  return (
    <div className="page-container">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="page-content">
        {/* Header */}
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
              Users
            </h1>
            <p style={{ color: '#7f8c8d', margin: 0 }}>
              {loading ? 'Loading…' : `${users.length} total user${users.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="users-header-buttons">
            <label className="btn btn-secondary" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
              📥 Import CSV
              <input type="file" accept=".csv" style={{ display: 'none' }} onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const text = await f.text();
                const lines = text.split('\n').filter(Boolean);
                const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                const idx = (k) => headers.indexOf(k);
                const rows = lines.slice(1).map(l => {
                  const c = l.split(',').map(x => x.trim().replace(/^"|"$/g, ''));
                  return {
                    firstName: c[idx('firstname')] || c[idx('first name')] || c[idx('first')],
                    lastName: c[idx('lastname')] || c[idx('last name')] || c[idx('last')],
                    phone: c[idx('phone')] || c[idx('phone number')],
                    email: c[idx('email')],
                  };
                }).filter(r => r.firstName && r.lastName);
                if (!rows.length) { alert('No valid rows found. CSV must have firstName, lastName columns'); return; }
                if (!window.confirm(`Import ${rows.length} users?`)) return;
                try {
                  const res = await userAdminApi.csvImport(rows, user?.id);
                  alert(`Created: ${res.created || res.data?.created}, Skipped: ${res.skipped || res.data?.skipped}`);
                  fetchUsers();
                } catch (er) { alert('Failed: ' + er.message); }
              }} />
            </label>
            <button
              className="btn btn-secondary"
              onClick={fetchUsers}
              disabled={loading}
              style={{ whiteSpace: 'nowrap' }}
            >
              {loading ? '⟳ Refreshing…' : '⟳ Refresh'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleOpenCreate}
              style={{ whiteSpace: 'nowrap' }}
            >
              + Create User
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => exportCSV(
                'users-export.csv',
                ['First Name', 'Last Name', 'Username', 'Phone', 'Email', 'Roles', 'Created'],
                filtered.map(u => [
                  u.firstName, u.lastName, u.username,
                  u.phoneNumber || '', u.email || '',
                  (u.roles || []).join(', '),
                  u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ''
                ])
              )}
              style={{ whiteSpace: 'nowrap' }}
            >
              Export CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>{error}</div>
        )}

        {/* Filters */}
        <div className="users-filter-bar">
          <input
            type="text"
            className="form-input"
            placeholder="🔍  Search by name, username, email or phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 260px', margin: 0 }}
          />
          <select
            className="form-input"
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            style={{ flex: '0 0 180px', margin: 0 }}
          >
            <option value="all">All Roles</option>
            {allRoles.map(r => (
              <option key={r} value={r}>
                {ROLE_COLORS[r]?.label || r}
              </option>
            ))}
          </select>
          {(search || roleFilter !== 'all') && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setSearch(''); setRoleFilter('all'); }}
            >
              Clear
            </button>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#7f8c8d', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={handleToggleShowArchived}
              style={{ accentColor: '#e74c3c' }}
            />
            Show Archived
          </label>
        </div>

        {/* Selection action bar */}
        {selectedIds.size > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            background: '#fff3cd', borderRadius: 8, marginBottom: 12, border: '1px solid #ffc107',
          }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#856404' }}>
              {selectedIds.size} selected
            </span>
            <button
              className="btn btn-sm"
              onClick={() => setShowArchiveConfirm(true)}
              style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '5px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Archive Selected
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedIds(new Set())}
            >
              Deselect All
            </button>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="loading-state" style={{ textAlign: 'center', padding: '4rem' }}>
            <div className="loading-spinner" style={{ margin: '0 auto 1rem' }}></div>
            <p style={{ color: '#7f8c8d' }}>Loading users…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: '4rem 2rem',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
            <h3 style={{ color: '#2c3e50', marginBottom: 8 }}>
              {users.length === 0 ? 'No Users Found' : 'No Matching Users'}
            </h3>
            <p style={{ color: '#7f8c8d' }}>
              {users.length === 0
                ? 'No users have registered yet.'
                : 'Try adjusting your search or filter.'}
            </p>
          </div>
        ) : (
          <div className="users-table-container">
            {/* Table header */}
            <div className="users-table-header">
              <div style={{ width: 32, flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onChange={handleSelectAll}
                  style={{ accentColor: '#3498db', cursor: 'pointer' }}
                />
              </div>
              <div></div>
              <div>User</div>
              <div>Username</div>
              <div>Phone</div>
              <div>VIP</div>
              <div>Roles</div>
              <div></div>
            </div>

            {/* Rows */}
            {filtered.map((u, idx) => {
              const isEditing = editingUserId === u.id;
              const isArchived = !!u.archivedAt;

              return (
                <div key={u.id}>
                  <div
                    className="users-table-row"
                    style={{
                      ...(isEditing ? { background: '#f0f7ff' } : {}),
                      ...(isArchived ? { opacity: 0.5, background: '#f8f8f8' } : {}),
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{ width: 32, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        onChange={() => handleToggleSelect(u.id)}
                        style={{ accentColor: '#3498db', cursor: 'pointer' }}
                      />
                    </div>

                    {/* Avatar */}
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      flexShrink: 0,
                    }}>
                      {getInitials(u)}
                    </div>

                    {/* Full name + email */}
                    <div>
                      <div style={{ fontWeight: 600, color: '#2c3e50', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.firstName || u.lastName
                          ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                          : u.username}
                      </div>
                      <div style={{ fontSize: 12, color: '#7f8c8d', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {u.email || '—'}
                      </div>
                    </div>

                    {/* Username */}
                    <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {u.username}
                    </div>

                    {/* Phone */}
                    <div style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>
                      {formatPhone(u.phoneNumber)}
                    </div>

                    {/* VIP */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: u.vipActive ? '#f39c12' : '#999',
                      }}>
                        {u.vipActive ? 'Yes' : 'No'}
                      </span>
                      {u.vip1 && (
                        <div style={{ fontSize: 10, color: u.vipActive ? '#888' : '#e74c3c', marginTop: 2, whiteSpace: 'nowrap' }}>
                          {u.vipExpiration
                            ? (u.vipActive
                              ? new Date(u.vipExpiration).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                              : 'Expired')
                            : 'Lifetime'}
                        </div>
                      )}
                    </div>

                    {/* Roles */}
                    <div>
                      {(u.roles || []).length > 0
                        ? u.roles.map(r => getRoleBadge(r))
                        : <span style={{ color: '#bdc3c7', fontSize: 13 }}>—</span>}
                    </div>

                    {/* Actions */}
                    <div style={{ textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {isArchived && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#e74c3c', background: 'rgba(231,76,60,0.1)', padding: '2px 8px', borderRadius: 10 }}>
                          Archived
                        </span>
                      )}
                      {isArchived ? (
                        <button
                          onClick={() => handleUnarchive(u.id)}
                          style={{
                            background: 'none', border: '1px solid #27ae60', borderRadius: 6,
                            padding: '4px 10px', fontSize: 12, color: '#27ae60', cursor: 'pointer',
                          }}
                        >
                          Unarchive
                        </button>
                      ) : !isEditing ? (
                        <button
                          onClick={() => navigate(`/users/${u.id}`)}
                          style={{
                            background: 'none',
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 12,
                            color: '#555',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { e.target.style.borderColor = '#3498db'; e.target.style.color = '#3498db'; }}
                          onMouseLeave={e => { e.target.style.borderColor = '#ddd'; e.target.style.color = '#555'; }}
                        >
                          Edit
                        </button>
                      ) : null}
                    </div>
                  </div>

                  {/* Inline role editor */}
                  {isEditing && (
                    <div style={{
                      padding: '12px 1.5rem 16px',
                      paddingLeft: 'calc(2.5rem + 1.5rem + 1rem)',
                      background: '#f0f7ff',
                      borderTop: '1px solid rgba(52, 152, 219, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      flexWrap: 'wrap',
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#555', marginRight: 4 }}>
                        Assign roles:
                      </span>
                      {(availableRoles.length > 0 ? availableRoles : Object.keys(ROLE_COLORS).map(n => ({ name: n }))).map(role => {
                        const roleName = role.name;
                        const isChecked = editingRoles.includes(roleName);
                        const roleStyle = ROLE_COLORS[roleName] || { bg: 'rgba(127,140,141,0.15)', color: '#7f8c8d', label: roleName };
                        return (
                          <label
                            key={roleName}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '5px 12px',
                              borderRadius: 20,
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: `2px solid ${isChecked ? roleStyle.color : '#ddd'}`,
                              background: isChecked ? roleStyle.bg : '#fff',
                              color: isChecked ? roleStyle.color : '#999',
                              transition: 'all 0.15s',
                              userSelect: 'none',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleRole(roleName)}
                              style={{ display: 'none' }}
                            />
                            <span style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              border: `2px solid ${isChecked ? roleStyle.color : '#ccc'}`,
                              background: isChecked ? roleStyle.color : '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              color: '#fff',
                              flexShrink: 0,
                            }}>
                              {isChecked ? '✓' : ''}
                            </span>
                            {roleStyle.label}
                          </label>
                        );
                      })}

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        <button
                          onClick={handleCancelEdit}
                          disabled={savingRoles}
                          style={{
                            background: '#fff',
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            padding: '6px 14px',
                            fontSize: 13,
                            color: '#555',
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveRoles}
                          disabled={savingRoles}
                          style={{
                            background: '#3498db',
                            border: 'none',
                            borderRadius: 6,
                            padding: '6px 14px',
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#fff',
                            cursor: savingRoles ? 'wait' : 'pointer',
                            opacity: savingRoles ? 0.7 : 1,
                          }}
                        >
                          {savingRoles ? 'Saving…' : 'Save Roles'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Footer count */}
            <div style={{
              padding: '0.75rem 1.5rem',
              borderTop: '1px solid #f0f0f0',
              fontSize: 13,
              color: '#7f8c8d',
              background: '#fafafa',
            }}>
              Showing {filtered.length} of {users.length} users
            </div>
          </div>
        )}

        {/* Toast */}
        {/* Archive Confirmation Modal */}
        {showArchiveConfirm && (
          <div className="modal-overlay" onClick={() => setShowArchiveConfirm(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
              <h3 style={{ margin: '0 0 12px', color: '#2c3e50' }}>Archive {selectedIds.size} User{selectedIds.size !== 1 ? 's' : ''}?</h3>
              <p style={{ color: '#7f8c8d', fontSize: 14, marginBottom: 20 }}>
                Archived users will be hidden from all lists but their data will be preserved. You can unarchive them later.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowArchiveConfirm(false)}
                  disabled={archiving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchiveSelected}
                  disabled={archiving}
                  style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                >
                  {archiving ? 'Archiving...' : 'Archive'}
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '12px 20px',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background: toast.type === 'success' ? '#27ae60' : '#e74c3c',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            zIndex: 9999,
            animation: 'fadeIn 0.2s ease',
          }}>
            {toast.message}
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={handleCloseCreate}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
              <div className="modal-header">
                <h3>Create User</h3>
                <button className="modal-close" onClick={handleCloseCreate}>&times;</button>
              </div>

              <form onSubmit={handleCreateUser}>
                <div className="modal-body">
                  {createError && (
                    <div style={{
                      background: 'rgba(231,76,60,0.1)',
                      border: '1px solid rgba(231,76,60,0.3)',
                      color: '#e74c3c',
                      padding: '10px 14px',
                      borderRadius: 8,
                      fontSize: 13,
                      marginBottom: 16,
                    }}>
                      {createError}
                    </div>
                  )}

                  <div className="users-create-form-grid">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={createForm.firstName}
                        onChange={e => handleCreateFormChange('firstName', e.target.value)}
                        placeholder="John"
                        required
                        autoFocus
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={createForm.lastName}
                        onChange={e => handleCreateFormChange('lastName', e.target.value)}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={createForm.phoneNumber}
                      onChange={e => handleCreateFormChange('phoneNumber', e.target.value)}
                      placeholder="(303) 555-1234"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={createForm.email}
                      onChange={e => handleCreateFormChange('email', e.target.value)}
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Password * <span style={{ fontWeight: 400, color: '#999', fontSize: 12 }}>(min 4 characters)</span></label>
                    <input
                      type="text"
                      className="form-input"
                      value={createForm.password}
                      onChange={e => handleCreateFormChange('password', e.target.value)}
                      placeholder="1234"
                      required
                      minLength={4}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Roles</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      {(availableRoles.length > 0 ? availableRoles : Object.keys(ROLE_COLORS).map(n => ({ name: n }))).map(role => {
                        const roleName = role.name;
                        const isChecked = createForm.roles.includes(roleName);
                        const roleStyle = ROLE_COLORS[roleName] || { bg: 'rgba(127,140,141,0.15)', color: '#7f8c8d', label: roleName };
                        return (
                          <label
                            key={roleName}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '5px 12px',
                              borderRadius: 20,
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              border: `2px solid ${isChecked ? roleStyle.color : '#ddd'}`,
                              background: isChecked ? roleStyle.bg : '#fff',
                              color: isChecked ? roleStyle.color : '#999',
                              transition: 'all 0.15s',
                              userSelect: 'none',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleCreateToggleRole(roleName)}
                              style={{ display: 'none' }}
                            />
                            <span style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              border: `2px solid ${isChecked ? roleStyle.color : '#ccc'}`,
                              background: isChecked ? roleStyle.color : '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 10,
                              color: '#fff',
                              flexShrink: 0,
                            }}>
                              {isChecked ? '✓' : ''}
                            </span>
                            {roleStyle.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseCreate}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating ? 'Creating…' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Users;
